'use client'

import { useState, useRef, useEffect, useCallback } from "react"
import {
  MessageCircle,
  X,
  Send,
  Sparkles,
  User,
  MapPin,
  Clock,
  Phone,
  ShoppingBag,
  Crown,
  ArrowUp,
} from "lucide-react"
import { SITE_CONFIG, RAYONS } from "@/lib/site-config"

/**
 * Chatbot SADIYA — Conseillère virtuelle de la maison
 *
 * Bouton flottant en bas à droite, ouvre un panneau de chat animé.
 * Réponses pré-programmées en français basées sur des mots-clés.
 * Base de connaissance : horaires, adresse, rayons, contact, livraison,
 * services de luxe, collection privilège, Maison Eva Diaw, fondateur.
 *
 * Marque : SADIYA STYLE CUIR — palette or / cuir / émeraude.
 */

type Msg = {
  id: string
  role: "bot" | "user"
  text: string
  chips?: string[]
}

const BOT_AVATAR = "S"

// ── Base de connaissances — réponses pré-programmées ────────────────────────
const QUICK_REPLIES = [
  "Horaires",
  "Adresse",
  "Les 9 rayons",
  "Contact",
  "Livraison",
  "Maison Eva Diaw",
]

function matchKeyword(input: string): string {
  const t = input.toLowerCase().trim()
  const has = (...ks: string[]) => ks.some((k) => t.includes(k))

  // Accueil / Bonjour
  if (has("bonjour", "salut", "bonsoir", "hello", "coucou", "bienvenue")) {
    return "Bonjour et bienvenue chez SADIYA STYLE CUIR. Je suis votre conseillère virtuelle. Je peux vous renseigner sur nos 9 rayons du monde, nos horaires, notre adresse, nos services de luxe ou la Maison Eva Diaw. Comment puis-je vous aider ?"
  }

  // Horaires
  if (has("heure", "horaire", "ouvert", "ferm", "ouvre", "ferme", "jours")) {
    return `Nos horaires d'ouverture :\n• ${SITE_CONFIG.contact.hours}\n• 7j/7, jours fériés inclus\n\nNous vous accueillons au ${SITE_CONFIG.address.line1} — ${SITE_CONFIG.address.zip} ${SITE_CONFIG.address.city} (${SITE_CONFIG.address.metro}).`
  }

  // Adresse / Localisation
  if (has("adresse", "ou se", "où se", "localis", "situé", "situe", "venir", "acces", "métro", "metro", "stalingrad", "villette")) {
    return `Nous sommes situés :\n${SITE_CONFIG.address.line1}\n${SITE_CONFIG.address.zip} ${SITE_CONFIG.address.city}\n${SITE_CONFIG.address.country}\n\n${SITE_CONFIG.address.metro}\n\nAccès : métro Stalingrad (lignes 2, 5, 7) — sortie Boulevard de la Villette.`
  }

  // ── Correspondances SPÉCIFIQUES d'abord (avant les génériques) ─────────

  // Collection Privilège — doit être vérifié AVANT "collection" générique
  // NB : "privilège" contient "è", donc on teste à la fois "privilè" et "privileg"
  if (has("privilè", "privileg", "privée", "privee", "excep", "rare", "pièces", "pieces", "edition", "édition")) {
    return `La Collection Privilège SADIYA :\n\nUne sélection de pièces d'exception choisies à la main par Eva Diaw : caftans brodés fil d'or de Fès, tapis Beni Ouarain tissés 6 mois, coffrets en cuir doré à la feuille, fontaines en zellige mauresque… Chaque pièce est accompagnée de sa carte d'authenticité numérotée et signée.\n\nAccédez à la Collection Privilège via le menu principal.`
  }

  // Eva Diaw — doit être vérifié tôt (avant "rayon" qui contient "curated" via description)
  if (has("eva", "diaw", "curatrice", "boutique eva", "maison eva")) {
    return `La Maison Eva Diaw :\n\nMaison de curation fondée par Eva Diaw, qui sélectionne pour SADIYA STYLE CUIR les plus belles pièces de mode, d'artisanat et d'épicerie fine issues des diasporas africaine, méditerranéenne et orientale.\n\n« Sélectionner, c'est raconter. Offrir, c'est accomplir. »\n\nChaque article dans nos rayons porte le sceau de qualité Eva Diaw.`
  }

  // Services de luxe
  if (has("service", "luxe", "prestation", "cadeau", "emballage", "personnalis", "rendez-vous")) {
    return `Nos 6 Services de Luxe — l'art de recevoir selon Eva Diaw :\n\n1. Emballage Signature Eva Diaw — offert dès 90€\n2. Livraison Gant Blanc Paris — dès 49€\n3. Carte d'Authenticité — offerte pour la Collection Privilège\n4. Curation Privée sur rendez-vous — 150€\n5. Personnalisation & Monogramme — dès 35€\n6. Atelier Provenance & Traçabilité — offert pour la Collection Privilège\n\nDécouvrez-les en détail dans la rubrique « Services de Luxe » du menu.`
  }

  // Livraison
  if (has("livraison", "livrer", "expedition", "expédition", "envoi", "commande", "livré")) {
    return `Nos options de livraison :\n• Retrait en magasin : gratuit, sous 2h\n• Livraison Paris intramuros (75) : à partir de 49€ — coursier en gants blancs\n• Livraison France métropolitaine : 9,90€ (offerte dès 120€)\n• Livraison UE : 24,90€ (délai 4-6 jours ouvrés)\n\nLe service « Livraison Gant Blanc Paris » est notre signature : présentation en gants blancs par coursier dédié, créneau garanti.`
  }

  // Contact
  if (has("contact", "telephone", "téléphone", "tel", "mail", "email", "joindre", "appeler")) {
    return `Pour nous joindre :\n• Téléphone : ${SITE_CONFIG.contact.phone}\n• WhatsApp : ${SITE_CONFIG.contact.whatsapp}\n• Email : ${SITE_CONFIG.contact.email}\n\nNous répondons sous 24h ouvrées. Pour une visite en personne, présentation au 9 Boulevard de la Villette, Paris 10e.`
  }

  // Paiement / Prix
  if (has("paiement", "payer", "prix", "tarif", "coute", "cb", "carte bancaire", "espece", "espèce", "chèque", "cheque")) {
    return `Moyens de paiement acceptés :\n• Cartes bancaires (Visa, Mastercard, Amex)\n• Espèces\n• Apple Pay / Google Pay\n• Paiement en 3x sans frais dès 150€\n• Chèques (à l'ordre de SADIYA STYLE CUIR)\n\nGamme de prix : de 5€ (épices) à 2 490€ (tapis Beni Ouarain XL, Collection Privilège).`
  }

  // Fondateur
  if (has("fondat", "fondatrice", "fondé", "fonde", "fondation", "princesse", "alimatou", "sadiya ndiaye", "qui est", "qui a fond", "histoire", "origine de")) {
    return `SADIYA STYLE CUIR a été fondée par La Princesse Alimatou Sadiya Ndiaye, Fondatrice & Directrice Artistique.\n\nLa maison incarne l'élégance du monde au cœur de Paris : 9 rayons internationaux, une curation d'auteur, un savoir-recevoir hérité du Téranga africain et de l'hospitalité orientale.\n\n« L'élégance du monde, au cœur de Paris. »`
  }

  // ── Puis les correspondances GÉNÉRIQUES ──────────────────────────────────

  // Rayons spécifiques — doit être vérifié AVANT "rayon" générique pour
  // attraper "Parlez-moi du rayon Trésor Méditerranée"
  for (const r of RAYONS) {
    const slugWords = r.slug.split("-")
    const nameWords = r.name.toLowerCase().split(/\s+/)
    if (
      has(r.name.toLowerCase(), r.subtitle.toLowerCase(), ...slugWords, ...nameWords)
    ) {
      return `Le rayon ${r.name} — ${r.subtitle} (${r.region}) :\n\n${r.description}\n\nCe rayon propose une sélection authentique choisie par la Maison Eva Diaw. Visitez-le en magasin ou explorez-le sur notre site.`
    }
  }

  // Rayons / Collections (générique)
  if (has("rayon", "collection", "catalogue", "produit", "article", "vente", "vende")) {
    const list = RAYONS.map((r, i) => `${i + 1}. ${r.name} — ${r.subtitle}`).join("\n")
    return `SADIYA STYLE CUIR vous propose 9 rayons du monde :\n\n${list}\n\nChaque rayon est soigneusement curated par la Maison Eva Diaw. Souhaitez-vous en savoir plus sur un rayon en particulier ?`
  }

  // Merci
  if (has("merci", "thanks", "thank", "remerc")) {
    return "C'est un plaisir. N'hésitez pas à me solliciter si vous avez d'autres questions — je reste à votre disposition. À bientôt chez SADIYA STYLE CUIR."
  }

  // Au revoir
  if (has("au revoir", "bye", "adieu", "à bientôt", "a bientot")) {
    return "Au revoir, et à très bientôt chez SADIYA STYLE CUIR — Boulevard de la Villette, Paris 10e."
  }

  // Fallback
  return "Je n'ai pas tout à fait saisi votre demande. Puis-je vous proposer ces sujets ? Vous pouvez me demander : nos horaires, notre adresse, les 9 rayons, les coordonnées de contact, les options de livraison, nos services de luxe, la Collection Privilège ou la Maison Eva Diaw."
}

// ── Composant ────────────────────────────────────────────────────────────────
export function ChatbotWidget() {
  const [open, setOpen] = useState(false)
  const [messages, setMessages] = useState<Msg[]>([
    {
      id: "welcome",
      role: "bot",
      text: "Bonjour, je suis la conseillère virtuelle SADIYA STYLE CUIR. Comment puis-je vous aider aujourd'hui ?",
      chips: QUICK_REPLIES,
    },
  ])
  const [input, setInput] = useState("")
  const [isTyping, setIsTyping] = useState(false)
  const [unread, setUnread] = useState(0)
  const [showTop, setShowTop] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Scroll en bas à chaque nouveau message
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [messages, isTyping])

  // Focus à l'ouverture
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 300)
    }
  }, [open])

  // Détection du scroll pour afficher le bouton "retour en haut"
  useEffect(() => {
    const onScroll = () => {
      setShowTop(window.scrollY > 480)
    }
    onScroll()
    window.addEventListener("scroll", onScroll, { passive: true })
    return () => window.removeEventListener("scroll", onScroll)
  }, [])

  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: "smooth" })
  }, [])

  // Reset unread quand on ouvre — géré dans le handler du bouton (évite le set-state-in-effect)

  const send = useCallback((raw?: string) => {
    const text = (raw ?? input).trim()
    if (!text) return

    const userMsg: Msg = {
      id: `u-${Date.now()}`,
      role: "user",
      text,
    }
    setMessages((m) => [...m, userMsg])
    setInput("")
    setIsTyping(true)

    // Réponse simulée avec délai (effet "conseillère qui tape")
    const delay = 600 + Math.random() * 600
    setTimeout(() => {
      const reply = matchKeyword(text)
      const botMsg: Msg = {
        id: `b-${Date.now()}`,
        role: "bot",
        text: reply,
        chips: reply.includes("Comment puis-je") || reply.includes("Souhaitez-vous")
          ? QUICK_REPLIES
          : undefined,
      }
      setMessages((m) => [...m, botMsg])
      setIsTyping(false)
      if (!open) setUnread((n) => n + 1)
    }, delay)
  }, [input, open])

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    send()
  }

  return (
    <>
      {/* ── Bouton "Retour en haut" — au-dessus du FAB chatbot ─────────────────
          Affiché uniquement :
          - si l'utilisateur a scrollé (showTop)
          - ET si le chat est fermé (open = false)
          Quand le chat s'ouvre, le bouton disparaît pour laisser place au
          panneau de chat qui occupe la zone au-dessus du FAB.
      */}
      <button
        type="button"
        onClick={scrollToTop}
        aria-label="Retour en haut"
        className={`fixed right-4 sm:right-6 bottom-24 z-40 h-12 w-12 rounded-full glass-gold border border-gold/60 shadow-luxury flex items-center justify-center text-cuir hover:gradient-gold hover:text-cuir transition-all duration-300 hover:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold/70 ${
          showTop && !open
            ? "opacity-100 translate-y-0 pointer-events-auto"
            : "opacity-0 translate-y-4 pointer-events-none"
        }`}
      >
        <ArrowUp className="h-5 w-5" />
        {/* Tooltip à gauche (pour éviter de sortir de l'écran à droite) */}
        <span
          className={`pointer-events-none absolute right-full mr-3 top-1/2 -translate-y-1/2 whitespace-nowrap rounded-md gradient-cuir px-3 py-1.5 text-xs font-medium tracking-wide text-primary-foreground shadow-luxury transition-all duration-200 ${
            showTop && !open
              ? "opacity-100 translate-x-0"
              : "opacity-0 translate-x-2"
          }`}
        >
          Haut de page
          <span className="absolute left-full top-1/2 -translate-y-1/2 border-4 border-transparent border-l-cuir" />
        </span>
      </button>

      {/* ── Bulle d'ouverture (FAB) ─────────────────────────────────────────── */}
      <button
        type="button"
        onClick={() => {
          setOpen((v) => {
            if (!v) setUnread(0) // reset unread à l'ouverture
            return !v
          })
        }}
        aria-label={open ? "Fermer le chat" : "Ouvrir le chat SADIYA"}
        className="fixed right-4 sm:right-6 bottom-6 z-50 h-14 w-14 rounded-full gradient-emerald-gold border-2 border-gold/70 shadow-luxury flex items-center justify-center text-primary-foreground hover:scale-110 transition-all duration-300 focus:outline-none focus-visible:ring-4 focus-visible:ring-gold/40"
      >
        {/* Anneau pulsé */}
        <span className="absolute inset-0 rounded-full border-2 border-gold/50 animate-ping-slow" />
        {open ? (
          <X className="h-6 w-6 relative" />
        ) : (
          <MessageCircle className="h-6 w-6 relative" />
        )}
        {/* Badge non-lus */}
        {!open && unread > 0 && (
          <span className="absolute -top-1 -right-1 h-5 min-w-5 px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center border-2 border-background">
            {unread}
          </span>
        )}
      </button>

      {/* ── Panneau de chat ────────────────────────────────────────────────── */}
      <div
        className={`fixed right-4 sm:right-6 bottom-24 z-50 w-[calc(100vw-2rem)] sm:w-[400px] max-w-[400px] h-[560px] max-h-[calc(100vh-8rem)] rounded-2xl overflow-hidden border border-gold/40 shadow-luxury bg-background flex flex-col transition-all duration-300 origin-bottom-right ${
          open
            ? "opacity-100 scale-100 translate-y-0 pointer-events-auto"
            : "opacity-0 scale-95 translate-y-4 pointer-events-none"
        }`}
        aria-hidden={!open}
        role="dialog"
        aria-label="Conseillère SADIYA"
      >
        {/* En-tête */}
        <div className="gradient-cuir px-4 py-3 flex items-center gap-3 border-b border-gold/40">
          <div className="relative h-10 w-10 rounded-full gradient-gold p-[2px] flex-shrink-0">
            <div className="h-full w-full rounded-full bg-cuir flex items-center justify-center">
              <span className="font-display text-lg font-bold text-gradient-gold">
                {BOT_AVATAR}
              </span>
            </div>
            {/* Point en ligne */}
            <span className="absolute -bottom-0 -right-0 h-3 w-3 rounded-full bg-emerald border-2 border-cuir" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-display text-base font-semibold text-primary-foreground leading-tight">
              Conseillère SADIYA
            </p>
            <p className="text-[11px] text-gold/90 flex items-center gap-1">
              <Sparkles className="h-3 w-3" />
              En ligne — Maison Eva Diaw
            </p>
          </div>
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label="Fermer"
            className="h-8 w-8 rounded-full bg-gold/10 hover:bg-gold/20 text-gold flex items-center justify-center transition-colors flex-shrink-0"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Corps — messages */}
        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto p-4 space-y-3 bg-gradient-to-b from-background to-muted/30"
        >
          {messages.map((m) => (
            <MessageBubble key={m.id} msg={m} onChip={(c) => send(c)} />
          ))}

          {isTyping && (
            <div className="flex items-end gap-2">
              <BotAvatar />
              <div className="rounded-2xl rounded-bl-sm bg-card border border-border px-4 py-3 shadow-sm">
                <div className="flex items-center gap-1">
                  <span className="h-1.5 w-1.5 rounded-full bg-gold animate-bounce-dot [animation-delay:-0.3s]" />
                  <span className="h-1.5 w-1.5 rounded-full bg-gold animate-bounce-dot [animation-delay:-0.15s]" />
                  <span className="h-1.5 w-1.5 rounded-full bg-gold animate-bounce-dot" />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Zone de saisie */}
        <form
          onSubmit={onSubmit}
          className="border-t border-gold/30 p-3 bg-background flex items-center gap-2"
        >
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Posez votre question…"
            className="flex-1 h-10 rounded-full bg-muted border border-border px-4 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-gold/60 focus:bg-background transition-colors"
            aria-label="Votre message"
          />
          <button
            type="submit"
            disabled={!input.trim()}
            aria-label="Envoyer"
            className="h-10 w-10 rounded-full gradient-emerald-gold border border-gold/60 text-primary-foreground flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-90 transition-opacity flex-shrink-0"
          >
            <Send className="h-4 w-4" />
          </button>
        </form>

        {/* Rappel coordonnées (pied) */}
        <div className="px-3 pb-2 pt-1 bg-background">
          <div className="flex items-center justify-between text-[10px] text-muted-foreground">
            <a
              href={`tel:${SITE_CONFIG.contact.phone.replace(/\s/g, "")}`}
              className="inline-flex items-center gap-1 hover:text-gold transition-colors"
            >
              <Phone className="h-3 w-3" />
              {SITE_CONFIG.contact.phone}
            </a>
            <span className="inline-flex items-center gap-1">
              <MapPin className="h-3 w-3" />
              Paris 10e
            </span>
            <span className="inline-flex items-center gap-1">
              <Clock className="h-3 w-3" />
              9h–21h
            </span>
          </div>
        </div>
      </div>
    </>
  )
}

// ── Sous-composants ──────────────────────────────────────────────────────────
function BotAvatar() {
  return (
    <div className="h-8 w-8 rounded-full gradient-gold p-[2px] flex-shrink-0">
      <div className="h-full w-full rounded-full bg-cuir flex items-center justify-center">
        <span className="font-display text-xs font-bold text-gradient-gold">S</span>
      </div>
    </div>
  )
}

function MessageBubble({ msg, onChip }: { msg: Msg; onChip: (chip: string) => void }) {
  if (msg.role === "user") {
    return (
      <div className="flex items-end gap-2 justify-end">
        <div className="rounded-2xl rounded-br-sm gradient-emerald-gold border border-gold/40 px-4 py-2.5 text-sm text-primary-foreground shadow-sm max-w-[80%]">
          {msg.text}
        </div>
        <div className="h-8 w-8 rounded-full bg-muted border border-border flex items-center justify-center flex-shrink-0">
          <User className="h-4 w-4 text-cuir/70" />
        </div>
      </div>
    )
  }

  return (
    <div className="flex items-end gap-2">
      <BotAvatar />
      <div className="flex flex-col gap-2 max-w-[85%]">
        <div className="rounded-2xl rounded-bl-sm bg-card border border-border px-4 py-2.5 text-sm text-foreground shadow-sm whitespace-pre-line">
          {msg.text}
        </div>
        {msg.chips && msg.chips.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {msg.chips.map((chip) => (
              <button
                key={chip}
                type="button"
                onClick={() => onChip(chip)}
                className="text-xs rounded-full border border-gold/50 bg-gold/5 px-3 py-1 text-cuir hover:bg-gold/15 hover:border-gold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gold/40"
              >
                {chip}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
